import type { Ping } from '../../shared/wire';

// leafletMap: the main tracker Leaflet instance, set once by ensureMap() in main.ts.
// Lifted here so that map/team-layer.ts (and future map/* modules that attach to
// the tracker map rather than owning their own instance) can read it without
// importing main (which would be circular).
let _leafletMap: any = null;
export function getLeafletMap(): any { return _leafletMap; }
export function setLeafletMap(m: any): void { _leafletMap = m; }

// shareViewerPings: the raw pings for the currently-open share-viewer session.
// Lifted here from main.ts so that replaySourcePings() (which reads this) can
// live in a module without importing back into main (which would be circular).
// Written by enterShareViewer / applyShareViewerPings in main.ts; read by
// replaySourcePings and renderShareViewerTrail.
let _shareViewerPings: Ping[] = [];
export function getShareViewerPings(): Ping[] { return _shareViewerPings; }
export function setShareViewerPings(pings: Ping[]): void { _shareViewerPings = pings; }
export function pushShareViewerPing(p: Ping): void { _shareViewerPings.push(p); }
export function clearShareViewerPings(): void { _shareViewerPings = []; }

export interface CohortEntry {
  token: string;
  animals: string[];
  created_at: number;
  pings: Ping[];
  truncated: boolean;
  lastT: number;
  // Effective cohort scope the server enforced for this device (Phase 2). The
  // pings above are already clamped+quantized to it; this is kept so the UI can
  // draw an honest "this is how this device exposes itself in the cohort" badge.
  // Undefined until a poll echoes it (the local seed has no server scope yet).
  cohort_scope?: { range: string; precision: string };
  // The device's display tag (ADR 0022); null when unset. Echoed by /recent.
  tag?: string | null;
}
export interface ActiveSession { id: number; started_at: number }

interface AppState {
  slots: (string | null)[];
  group: (string | null)[];
  targetOverride: { kind: 'group' | 'device'; idx: number } | null;
  recording: boolean;
  cadenceSec: number;
  selectedSession: string;
  viewMode: 'sessions' | 'ungrouped' | 'devices';
  shareSel: { range: number; precision: number; expires: number };
  mintedShare: Map<string, { share_token: string; expires_at: number | null }>;
  mintInFlight: Promise<unknown> | null;
  geoRequesting: boolean;
  geoError: string | null;
  cohort: Map<string, CohortEntry>;
  hiddenCohort: Set<string>;
  activeSession: ActiveSession | null;
}
export const state: AppState = {
  slots: [null,null,null],
  group: [null,null],            // optional 2-animal group prefix
  targetOverride: null,          // {kind:'group'|'device', idx} when user clicks a specific slot
  recording: false,
  cadenceSec: 10,
  selectedSession: 's0',  // newest first
  viewMode: 'sessions',          // 'sessions' | 'ungrouped' | 'devices'
  shareSel: { range:1, precision:1, expires:2 },
  // Cache for the share-URL mint. Key is `${device_token}|${range}.${precision}.${expires}`
  // — the device token is part of the key so switching devices never reuses
  // another device's share token. Value is the API `{share_token, expires_at}`.
  // buildShareUrl() evicts an entry once its share is expired (or within the
  // grace window) so re-sharing the same settings never hands back a dead link.
  mintedShare: new Map(),
  mintInFlight: null,            // Promise | null — current mint request
  geoRequesting: false,          // between "start recording" click and first fix
  geoError: null,                // null | 'denied' | 'unavailable' | 'timeout' | 'unsupported'
  // Cohort: every device sharing this device's 2-animal group prefix,
  // populated by /api/v1/groups/:g1-:g2/recent polling. Keyed by token.
  // Entry shape: {token, animals, created_at, pings: [{t,lat,lng}], truncated, lastT}.
  cohort: new Map(),
  // Viewer-local "By device" hide set: cohort tokens the viewer has toggled
  // off the map. EPHEMERAL render state only — not persisted, not sent to the
  // server, never visible to peers, and NOT a privacy control (the cohort is
  // semi-public; see 2026-05-22-locus-cohort-coop-view.md). The local device
  // is never added here — it stays pinned as the "you are here" anchor.
  hiddenCohort: new Set(),
  // Currently open server-tracked session for the local device, or null.
  // {id: number, started_at: epoch_ms}. Mirrored to localStorage so a
  // mid-recording reload resumes without a duplicate /sessions/start.
  activeSession: null,
};
